import React, { useEffect, useState, useCallback, useMemo } from "react";
import { SlidersHorizontal, Plus, Pencil, Trash2, Loader2, Award, AlertOctagon, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useReference } from "@/lib/useReference";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";

const EMPTY = { scope: "district", district_id: "", campus_id: "", team_id: "", name: "", category: "merit", value_type: "single", value: "1", min: "1", max: "5" };

function describeValue(p) { return p.value_type === "range" ? `${p.min}–${p.max}` : `${p.value}`; }

export default function MeritPresets() {
  const navigate = useNavigate();
  const { ref } = useReference();
  // Team types are derived from the teams in the database (code -> name), so every
  // current or future team type is available automatically without a code change.
  const TYPES = useMemo(() => {
    const map = new Map();
    (ref.teams || []).forEach((t) => { if (t.code && !map.has(t.code)) map.set(t.code, t.name || t.code); });
    return [...map.entries()].map(([v, l]) => ({ v, l })).sort((a, b) => a.l.localeCompare(b.l));
  }, [ref.teams]);
  const [type, setType] = useState("");
  useEffect(() => { if (!type && TYPES.length) setType(TYPES[0].v); }, [TYPES, type]);
  const [fDistrict, setFDistrict] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!type) { setLoading(false); return; }
    setLoading(true);
    try { const { data } = await api.get(`/admin/merit-presets?team_type=${type}`); setItems(data.items || []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const districtName = (id) => ref.districts.find((d) => d.id === id)?.name || "District";
  const teamById = (id) => ref.teams.find((t) => t.id === id);
  const shown = items.filter((o) => fDistrict === "all" || o.district_id === fDistrict);
  const formCampuses = ref.campuses.filter((c) => c.district_id === form.district_id);
  const formTeams = ref.teams.filter((t) => t.campus_id === form.campus_id && t.code === type);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, district_id: (fDistrict !== "all" ? fDistrict : ref.districts[0]?.id) || "" }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ scope: p.team_id ? "team" : "district", district_id: p.district_id, campus_id: teamById(p.team_id)?.campus_id || "", team_id: p.team_id || "", name: p.name, category: p.category, value_type: p.value_type, value: String(p.value ?? 1), min: String(p.min ?? 1), max: String(p.max ?? 5) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter a preset name"); return; }
    if (!form.district_id) { toast.error("Select a district"); return; }
    if (form.scope === "team" && !form.team_id) { toast.error("Select a team"); return; }
    const base = { name: form.name.trim(), category: form.category, value_type: form.value_type };
    if (form.value_type === "single") base.value = parseInt(form.value, 10);
    else { base.min = parseInt(form.min, 10); base.max = parseInt(form.max, 10); }
    setSaving(true);
    try {
      if (editing) { await api.put(`/admin/merit-presets/${editing.id}`, base); toast.success("Preset updated"); }
      else { await api.post("/admin/merit-presets", { team_type: type, district_id: form.district_id, team_id: form.scope === "team" ? form.team_id : null, ...base }); toast.success("Preset created"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/merit-presets/${del.id}`); toast.success("Preset deleted"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  const typeLabel = TYPES.find((t) => t.v === type)?.l || type;
  const scopeLabel = (p) => p.team_id ? (teamById(p.team_id)?.name || "Team") : "All teams in district";

  const Group = ({ title, list, Icon, tone }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Icon className={`h-4 w-4 ${tone}`} /> {title} <span className="text-slate-400">({list.length})</span></div>
      {list.length === 0 ? <p className="text-xs text-slate-400">No {title.toLowerCase()} presets for this selection.</p> : list.map((p) => (
        <Card key={p.id} className="flex items-center justify-between p-3" data-testid={`preset-row-${p.id}`}>
          <div className="flex items-center gap-3">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${p.category === "merit" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{describeValue(p)}</span>
            <div>
              <p className="font-semibold text-slate-800">{p.name}</p>
              <p className="text-xs text-slate-400">{p.value_type === "range" ? `Range ${p.min}–${p.max}` : `Fixed ${p.value}`} · {districtName(p.district_id)} · {scopeLabel(p)}{p.is_active === false ? " · inactive" : ""}</p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`preset-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setDel(p)} data-testid={`preset-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div>
      <button onClick={() => navigate("/admin/merits")} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="back-to-merits"><ArrowLeft className="h-4 w-4" /> Back to Merit System</button>
      <PageHeader title="Merit Options" subtitle="Set the merit & demerit policy per district and team type. District-wide presets apply to every team of that type."
        actions={<Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-preset-btn"><Plus className="mr-2 h-4 w-4" /> New preset</Button>} />

      <div className="mb-4 flex items-center gap-2">
        <Label className="text-sm font-semibold text-slate-700">Program</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-56" data-testid="preset-type-select"><SelectValue placeholder="Select program" /></SelectTrigger>
          <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v} data-testid={`preset-type-${t.v}`}>{t.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={fDistrict} onValueChange={setFDistrict}>
          <SelectTrigger className="w-56" data-testid="preset-district-filter"><SelectValue placeholder="District" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All districts</SelectItem>
            {ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : shown.length === 0 ? (
        <EmptyState title={`No ${typeLabel} presets`} description="Add merit or demerit presets for this team type and district." testId="presets-empty" />
      ) : (
        <div className="grid gap-6 md:grid-cols-2" data-testid="presets-list">
          <Group title="Merits" list={shown.filter((p) => p.category === "merit")} Icon={Award} tone="text-emerald-600" />
          <Group title="Demerits" list={shown.filter((p) => p.category === "demerit")} Icon={AlertOctagon} tone="text-red-600" />
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="preset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit preset" : `New ${typeLabel} preset`}</DialogTitle>
            <DialogDescription>District-wide applies to every {typeLabel} team in the district; team scope applies to one team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <>
                <div className="space-y-2"><Label>Scope</Label>
                  <Select value={form.scope} onValueChange={(v) => setForm((f) => ({ ...f, scope: v, campus_id: "", team_id: "" }))}>
                    <SelectTrigger data-testid="preset-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="district">District-wide (all {typeLabel} teams)</SelectItem>
                      <SelectItem value="team">Specific team</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>District</Label>
                  <Select value={form.district_id} onValueChange={(v) => setForm((f) => ({ ...f, district_id: v, campus_id: "", team_id: "" }))}>
                    <SelectTrigger data-testid="preset-district-select"><SelectValue placeholder="District" /></SelectTrigger>
                    <SelectContent>{ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.scope === "team" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Campus</Label>
                      <Select value={form.campus_id} onValueChange={(v) => setForm((f) => ({ ...f, campus_id: v, team_id: "" }))} disabled={!form.district_id}>
                        <SelectTrigger data-testid="preset-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
                        <SelectContent>{formCampuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Team</Label>
                      <Select value={form.team_id} onValueChange={(v) => setForm((f) => ({ ...f, team_id: v }))} disabled={!form.campus_id}>
                        <SelectTrigger data-testid="preset-team-select"><SelectValue placeholder="Team" /></SelectTrigger>
                        <SelectContent>{formTeams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Tardy to practice" data-testid="preset-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="preset-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="merit">Merit</SelectItem><SelectItem value="demerit">Demerit</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Value type</Label>
                <Select value={form.value_type} onValueChange={(v) => setForm((f) => ({ ...f, value_type: v }))}>
                  <SelectTrigger data-testid="preset-valuetype-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="single">Single number</SelectItem><SelectItem value="range">Range (min–max)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {form.value_type === "single" ? (
              <div className="space-y-2"><Label>Value</Label><Input type="number" min={1} max={100} value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} data-testid="preset-value-input" /></div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Minimum</Label><Input type="number" min={1} max={100} value={form.min} onChange={(e) => setForm((f) => ({ ...f, min: e.target.value }))} data-testid="preset-min-input" /></div>
                <div className="space-y-2"><Label>Maximum</Label><Input type="number" min={1} max={100} value={form.max} onChange={(e) => setForm((f) => ({ ...f, max: e.target.value }))} data-testid="preset-max-input" /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="preset-submit-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Create preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete preset?"
        description={del ? `"${del.name}" will be removed from coaches' dropdown. Existing cases keep their values.` : ""}
        confirmText="Delete" destructive onConfirm={remove} testId="preset-delete-dialog" />
    </div>
  );
}
